import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, X, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AgencyLogo } from "@/components/Navbar";
import { useSEO } from "@/hooks/useSEO";
import { translations } from "@/i18n/translations";

const TABS = ["overview", "talent", "images", "content", "applications", "enquiries", "contacts"];
const STATUSES = ["new", "reviewed", "contacted", "archived"];
const UPLOADS = `${process.env.REACT_APP_BACKEND_URL}/api/uploads`;

const IMAGE_SLOTS = [
  { key: "hero", label: "Homepage — Hero Picture", def: "https://images.unsplash.com/photo-1613915617430-8ab0fd7c6baf?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { key: "home_montreal", label: "Homepage — Philosophy (Montréal)", def: "https://images.unsplash.com/photo-1616034328430-a1ec65cabb01?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { key: "div_models", label: "Homepage — Models Division Card", def: "https://images.unsplash.com/photo-1516575150278-77136aed6920?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { key: "div_actors", label: "Homepage — Actors Division Card", def: "https://images.unsplash.com/photo-1654765437547-6b572f52ee1a?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { key: "div_voice", label: "Homepage — Voice Division Card", def: "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { key: "campaign_1", label: "Homepage — Project 1", def: "https://images.unsplash.com/photo-1571513800374-df1bbe650e56?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { key: "campaign_2", label: "Homepage — Project 2", def: "https://images.unsplash.com/photo-1662532577856-e8ee8b138a8b?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { key: "campaign_3", label: "Homepage — Project 3", def: "https://images.unsplash.com/photo-1613915617430-8ab0fd7c6baf?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { key: "campaign_4", label: "Homepage — Project 4", def: "https://images.unsplash.com/flagged/photo-1570733117311-d990c3816c47?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { key: "about_1", label: "About — Image 1", def: "https://images.unsplash.com/photo-1616034328430-a1ec65cabb01?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { key: "about_2", label: "About — Image 2", def: "https://images.unsplash.com/photo-1723921027368-c19d7f757fbd?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { key: "founder", label: "About — Founder Portrait (Cru)", def: "" },
];

const CONTENT_GROUPS = [
  {
    group: "Homepage",
    keys: ["home.eyebrow", "home.hero1", "home.hero2", "home.hero3", "home.heroSub", "home.divModelsCopy", "home.divActorsCopy", "home.divVoiceCopy", "home.quote", "home.philosophyBody", "home.ctaBand"],
  },
  {
    group: "Pages",
    keys: ["soon.title", "soon.body", "nf.castingOpen", "nf.castingBody", "nf.intro", "about.intro", "about.quote", "about.missionBody", "about.cta", "footer.tagline"],
  },
  {
    group: "Contact & Social",
    keys: ["site.email", "contact.email.models", "contact.email.actors", "contact.email.voice", "contact.email.submissions", "contact.email.general", "contact.email.press", "social.instagram"],
  },
  {
    group: "Founder Biography",
    keys: ["about.founderName", "about.founderRole", "about.founderBio1", "about.founderBio2", "about.founderBio3", "about.founderBio4", "about.founderBio5", "about.founderBio6"],
  },
];

const inputCls =
  "w-full border border-white/15 bg-black/40 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-gold";

const ContentField = ({ k, value, onSave }) => {
  const defEn = translations.en[k] ?? "";
  const defFr = translations.fr[k] ?? "";
  const [en, setEn] = useState(value?.en ?? defEn);
  const [fr, setFr] = useState(value?.fr ?? defFr);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setEn(value?.en ?? defEn);
    setFr(value?.fr ?? defFr);
  }, [value, k, defEn, defFr]);

  const dirty = en !== (value?.en ?? defEn) || fr !== (value?.fr ?? defFr);

  const save = async () => {
    setSaving(true);
    await onSave(k, { en, fr });
    setSaving(false);
  };

  const isEmail = k.includes("email") || k.includes("social");

  return (
    <div data-testid={`content-field-${k}`} className="border border-white/10 bg-[#121212] p-5">
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{k}</span>
        {value && <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/35">Customized</span>}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="block">
          <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">English</span>
          {isEmail ? (
            <input value={en} onChange={(e) => setEn(e.target.value)} className={`${inputCls} font-mono text-xs`} />
          ) : (
            <textarea rows={2} value={en} onChange={(e) => setEn(e.target.value)} className={`${inputCls} resize-none text-xs`} />
          )}
        </label>
        <label className="block">
          <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">Français</span>
          {isEmail ? (
            <input value={fr} onChange={(e) => setFr(e.target.value)} className={`${inputCls} font-mono text-xs`} />
          ) : (
            <textarea rows={2} value={fr} onChange={(e) => setFr(e.target.value)} className={`${inputCls} resize-none text-xs`} />
          )}
        </label>
      </div>
      <button
        data-testid={`content-save-${k}`}
        onClick={save}
        disabled={!dirty || saving}
        className="mt-3 bg-ivory px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold disabled:opacity-30"
      >
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
};

const emptyTalent = {
  name: "", division: "MODELS", categories: "", location: "", languages: "",
  skills: "", image_url: "", gallery: [], bio: "", height: "", bust_chest: "",
  waist: "", hips: "", shoes: "", eyes: "", hair: "", union_status: "",
  vocal_range: "", featured: false, new_face: false,
};

const toForm = (t) => ({
  name: t.name || "", division: t.division || "MODELS",
  categories: (t.categories || []).join(", "), location: t.location || "",
  languages: (t.languages || []).join(", "), skills: (t.skills || []).join(", "),
  image_url: t.image_url || "", gallery: t.gallery || [], bio: t.bio || "", height: t.height || "",
  bust_chest: t.bust_chest || "", waist: t.waist || "", hips: t.hips || "",
  shoes: t.shoes || "", eyes: t.eyes || "", hair: t.hair || "",
  union_status: t.union_status || "", vocal_range: t.vocal_range || "",
  featured: !!t.featured, new_face: !!t.new_face,
});

const fromForm = (f) => ({
  name: f.name, division: f.division,
  categories: f.categories.split(",").map((s) => s.trim()).filter(Boolean),
  location: f.location || null,
  languages: f.languages.split(",").map((s) => s.trim()).filter(Boolean),
  skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean),
  image_url: f.image_url || null, gallery: f.gallery, bio: f.bio || null, height: f.height || null,
  bust_chest: f.bust_chest || null, waist: f.waist || null, hips: f.hips || null,
  shoes: f.shoes || null, eyes: f.eyes || null, hair: f.hair || null,
  union_status: f.union_status || null, vocal_range: f.vocal_range || null,
  featured: f.featured, new_face: f.new_face,
});

const StatusSelect = ({ id, value, onChange, kind }) => (
  <select
    data-testid={`${kind}-status-${id}`}
    value={value}
    onChange={(e) => onChange(id, e.target.value)}
    className="border border-white/15 bg-black/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white outline-none focus:border-gold"
  >
    {STATUSES.map((s) => (
      <option key={s} value={s} className="bg-[#121212]">{s}</option>
    ))}
  </select>
);

const SlotCard = ({ slot, label, def, saved, onSave }) => {
  const [url, setUrl] = useState(saved || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => setUrl(saved || ""), [saved]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd);
      await onSave(slot, `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`site-image-card-${slot}`} className="border border-white/10 bg-[#121212] p-4">
      <div className="overflow-hidden border border-white/10">
        {saved || def ? (
          <img src={saved || def} alt={label} className="aspect-[16/10] w-full object-cover object-top" />
        ) : (
          <div className="flex aspect-[16/10] w-full items-center justify-center font-mono text-[10px] uppercase tracking-[0.2em] text-white/25">
            No image yet
          </div>
        )}
      </div>
      <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{label}</div>
      <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.15em] text-white/35">
        {saved ? "Custom image active" : "Default image"}
      </div>
      <input
        data-testid={`site-image-url-${slot}`}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="Paste image URL…"
        className={`${inputCls} mt-3 font-mono text-[11px]`}
      />
      <div className="mt-3 flex gap-2">
        <label
          data-testid={`site-image-upload-${slot}`}
          className="flex flex-1 cursor-pointer items-center justify-center gap-2 border border-dashed border-white/25 bg-black/30 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 transition-colors hover:border-gold hover:text-gold"
        >
          <Upload className="h-3.5 w-3.5" />
          {busy ? "Uploading…" : "Upload"}
          <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        <button
          data-testid={`site-image-save-${slot}`}
          onClick={() => url && onSave(slot, url)}
          disabled={!url}
          className="flex-1 bg-ivory px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold disabled:opacity-40"
        >
          Save URL
        </button>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  useSEO("Dashboard — Diamotims Agency CMS");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [talent, setTalent] = useState([]);
  const [applications, setApplications] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [siteImages, setSiteImages] = useState({});
  const [siteContent, setSiteContent] = useState({});
  const [editing, setEditing] = useState(null); // null | "new" | talent id
  const [form, setForm] = useState(emptyTalent);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/talent").then((r) => setTalent(r.data)).catch(() => {});
    api.get("/admin/applications").then((r) => setApplications(r.data)).catch(() => {});
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/site-images").then((r) => setSiteImages(r.data || {})).catch(() => {});
    api.get("/site-content").then((r) => setSiteContent(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => load(), [load]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const saveSlot = async (slot, url) => {
    try {
      await api.put(`/admin/site-images/${slot}`, { url });
      setSiteImages((prev) => ({ ...prev, [slot]: url }));
      toast.success("Image updated — live on the site now");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Save failed"));
    }
  };

  const saveContent = async (key, val) => {
    try {
      await api.put(`/admin/site-content/${key}`, val);
      setSiteContent((prev) => ({ ...prev, [key]: val }));
      toast.success("Text updated — visitors see it on next page load");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Save failed"));
    }
  };

  const uploadPhoto = async (file, target = "main") => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd);
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setForm((f) =>
        target === "gallery" ? { ...f, gallery: [...f.gallery, url] } : { ...f, image_url: url }
      );
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Photo upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImage = (url) =>
    setForm((f) => ({ ...f, gallery: f.gallery.filter((u) => u !== url) }));

  const saveTalent = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/admin/talent", fromForm(form));
        toast.success("Talent added");
      } else {
        await api.put(`/admin/talent/${editing}`, fromForm(form));
        toast.success("Talent updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Save failed"));
    }
  };

  const deleteTalent = async (id) => {
    if (!window.confirm("Delete this talent profile permanently?")) return;
    await api.delete(`/admin/talent/${id}`).catch(() => toast.error("Delete failed"));
    load();
  };

  const patchStatus = (kind, setter) => async (id, status) => {
    await api.patch(`/admin/${kind}/${id}/status`, { status }).catch(() => toast.error("Update failed"));
    setter((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-obsidian">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <AgencyLogo className="h-10 w-auto" />
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-gold">CMS</span>
          </div>
          <nav className="flex flex-wrap items-center gap-1">
            {TABS.map((t) => (
              <button
                key={t}
                data-testid={`admin-tab-${t}`}
                onClick={() => setTab(t)}
                className={`px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${tab === t ? "bg-ivory text-black" : "text-white/55 hover:text-gold"}`}
              >
                {t}
              </button>
            ))}
            <button data-testid="admin-logout" onClick={handleLogout} className="ml-2 flex items-center gap-2 border border-white/15 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 transition-colors hover:border-gold hover:text-gold">
              <LogOut className="h-3.5 w-3.5" /> {user?.email}
            </button>
          </nav>
        </div>
      </header>

      <div className="mx-auto max-w-[1600px] px-4 py-10 sm:px-8">
        {tab === "overview" && stats && (
          <div data-testid="admin-overview" className="grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-6">
            {[["Talent", stats.talent], ["Models", stats.models], ["Actors", stats.actors], ["Voice", stats.voice], ["New Applications", stats.applications_new], ["New Enquiries", stats.enquiries_new]].map(([label, v]) => (
              <div key={label} className="bg-[#0A0A0A] p-6">
                <div className="font-mono text-4xl font-bold tracking-tighter text-white">{v}</div>
                <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.25em] text-white/45">{label}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "images" && (
          <div data-testid="admin-images-tab">
            <h2 className="font-display text-3xl font-light uppercase tracking-tight text-ivory">Website Pictures</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/50">
              Change any picture on the website: upload a photo from your
              computer or paste an image link, then save. The site updates
              instantly. Talent profile photos are managed in the Talent tab.
            </p>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {IMAGE_SLOTS.map((s) => (
                <SlotCard key={s.key} slot={s.key} label={s.label} def={s.def} saved={siteImages[s.key]} onSave={saveSlot} />
              ))}
            </div>
          </div>
        )}

        {tab === "content" && (
          <div data-testid="admin-content-tab">
            <h2 className="font-display text-3xl font-light uppercase tracking-tight text-ivory">Website Text</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/50">
              Edit the words on the website in both languages. Each field shows
              English and French — change one or both, then save. Visitors see
              your text on their next page load.
            </p>
            {CONTENT_GROUPS.map((g) => (
              <div key={g.group} className="mt-10">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{g.group}</div>
                <div className="mt-4 space-y-4">
                  {g.keys.map((k) => (
                    <ContentField key={k} k={k} value={siteContent[k]} onSave={saveContent} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "talent" && (
          <div data-testid="admin-talent-tab">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-display text-3xl font-light uppercase tracking-tight text-ivory">Roster</h2>
              <button
                data-testid="admin-talent-add"
                onClick={() => { setForm(emptyTalent); setEditing("new"); }}
                className="flex items-center gap-2 bg-ivory px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-black transition-colors hover:bg-gold"
              >
                <Plus className="h-3.5 w-3.5" /> Add Talent
              </button>
            </div>

            {editing !== null && (
              <form data-testid="admin-talent-form" onSubmit={saveTalent} className="mb-8 border border-gold/30 bg-[#121212] p-6">
                <div className="mb-4 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                    {editing === "new" ? "New Talent" : `Editing ${editing}`}
                  </span>
                  <button type="button" data-testid="admin-talent-cancel" onClick={() => setEditing(null)} aria-label="Cancel" className="text-white/50 hover:text-gold">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <input data-testid="talent-form-name" required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} />
                  <select data-testid="talent-form-division" value={form.division} onChange={set("division")} className={`${inputCls} appearance-none`}>
                    {["MODELS", "ACTORS", "VOICE"].map((d) => (
                      <option key={d} value={d} className="bg-[#121212]">{d}</option>
                    ))}
                  </select>
                  <input data-testid="talent-form-categories" placeholder="Categories (comma separated)" value={form.categories} onChange={set("categories")} className={inputCls} />
                  <input data-testid="talent-form-location" placeholder="Location" value={form.location} onChange={set("location")} className={inputCls} />
                  <input data-testid="talent-form-languages" placeholder="Languages (comma separated)" value={form.languages} onChange={set("languages")} className={inputCls} />
                  <input data-testid="talent-form-skills" placeholder="Skills (comma separated)" value={form.skills} onChange={set("skills")} className={inputCls} />
                  <div className="sm:col-span-2 lg:col-span-3">
                    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Profile Photo</span>
                    <div className="flex flex-wrap items-center gap-3">
                      {form.image_url && (
                        <img src={form.image_url} alt="Preview" className="h-16 w-12 border border-white/15 object-cover object-top" />
                      )}
                      <label
                        data-testid="talent-form-photo-upload"
                        className="flex cursor-pointer items-center gap-2 border border-dashed border-white/25 bg-black/30 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 transition-colors hover:border-gold hover:text-gold"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        {uploading ? "Uploading…" : "Upload photo"}
                        <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} />
                      </label>
                      <input
                        data-testid="talent-form-image"
                        placeholder="…or paste an image URL"
                        value={form.image_url}
                        onChange={set("image_url")}
                        className={`${inputCls} min-w-[220px] flex-1`}
                      />
                    </div>
                  </div>
                  <div className="sm:col-span-2 lg:col-span-3">
                    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Gallery ({form.gallery.length})</span>
                    <div className="flex flex-wrap items-center gap-3">
                      {form.gallery.map((url, i) => (
                        <span key={url} className="relative">
                          <img src={url} alt={`Gallery ${i + 1}`} className="h-16 w-12 border border-white/15 object-cover object-top" />
                          <button
                            type="button"
                            data-testid={`talent-form-gallery-remove-${i}`}
                            aria-label={`Remove gallery photo ${i + 1}`}
                            onClick={() => removeGalleryImage(url)}
                            className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center border border-white/20 bg-black text-white/60 hover:border-red-400 hover:text-red-400"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                      <label
                        data-testid="talent-form-gallery-upload"
                        className="flex cursor-pointer items-center gap-2 border border-dashed border-white/25 bg-black/30 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 transition-colors hover:border-gold hover:text-gold"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        {uploading ? "Uploading…" : "Add gallery photo"}
                        <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => { uploadPhoto(e.target.files?.[0], "gallery"); e.target.value = ""; }} />
                      </label>
                    </div>
                  </div>
                  <div className="sm:col-span-2 lg:col-span-3">
                    <div className="mb-2 font-mono text-[9px] uppercase tracking-[0.3em] text-gold/70">Measurements</div>
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                      <input data-testid="talent-form-height" placeholder="Height" value={form.height} onChange={set("height")} className={inputCls} />
                      <input data-testid="talent-form-bust-chest" placeholder="Bust / Chest" value={form.bust_chest} onChange={set("bust_chest")} className={inputCls} />
                      <input data-testid="talent-form-waist" placeholder="Waist" value={form.waist} onChange={set("waist")} className={inputCls} />
                      <input data-testid="talent-form-hips" placeholder="Hips" value={form.hips} onChange={set("hips")} className={inputCls} />
                      <input data-testid="talent-form-shoes" placeholder="Shoes" value={form.shoes} onChange={set("shoes")} className={inputCls} />
                      <input data-testid="talent-form-eyes" placeholder="Eyes" value={form.eyes} onChange={set("eyes")} className={inputCls} />
                      <input data-testid="talent-form-hair" placeholder="Hair" value={form.hair} onChange={set("hair")} className={inputCls} />
                    </div>
                  </div>
                  <input data-testid="talent-form-union" placeholder="Union status (actors)" value={form.union_status} onChange={set("union_status")} className={inputCls} />
                  <input data-testid="talent-form-vocal" placeholder="Vocal range (voice)" value={form.vocal_range} onChange={set("vocal_range")} className={inputCls} />
                  <textarea data-testid="talent-form-bio" placeholder="Biography" rows={2} value={form.bio} onChange={set("bio")} className={`${inputCls} resize-none sm:col-span-2 lg:col-span-3`} />
                  <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                    <input data-testid="talent-form-featured" type="checkbox" checked={form.featured} onChange={set("featured")} className="h-4 w-4 accent-[#D4AF37]" /> Featured
                  </label>
                  <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                    <input data-testid="talent-form-new-face" type="checkbox" checked={form.new_face} onChange={set("new_face")} className="h-4 w-4 accent-[#D4AF37]" /> New Face
                  </label>
                </div>
                <button data-testid="talent-form-save" type="submit" className="mt-5 bg-gold px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-black transition-colors hover:bg-ivory">
                  Save Profile
                </button>
              </form>
            )}

            <div className="overflow-x-auto border border-white/10">
              <table className="w-full min-w-[720px] text-left">
                <thead>
                  <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">
                    <th className="p-4">Talent</th><th className="p-4">Division</th><th className="p-4">Categories</th><th className="p-4">Location</th><th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {talent.map((t) => (
                    <tr key={t.id} data-testid={`admin-talent-row-${t.id}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.03]">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {t.image_url && <img src={t.image_url} alt="" className="h-10 w-8 object-cover object-top" />}
                          <span className="font-syne text-sm font-semibold uppercase tracking-wide text-white">{t.name}</span>
                          {t.featured && <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-gold">Featured</span>}
                        </div>
                      </td>
                      <td className="p-4 font-mono text-[10px] uppercase text-white/60">{t.division}</td>
                      <td className="p-4 font-mono text-[10px] text-white/50">{(t.categories || []).join(", ")}</td>
                      <td className="p-4 font-mono text-[10px] text-white/50">{t.location}</td>
                      <td className="p-4 text-right">
                        <button data-testid={`admin-talent-edit-${t.id}`} onClick={() => { setForm(toForm(t)); setEditing(t.id); }} aria-label={`Edit ${t.name}`} className="mr-2 inline-flex h-8 w-8 items-center justify-center border border-white/15 text-white/60 transition-colors hover:border-gold hover:text-gold">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={`admin-talent-delete-${t.id}`} onClick={() => deleteTalent(t.id)} aria-label={`Delete ${t.name}`} className="inline-flex h-8 w-8 items-center justify-center border border-white/15 text-white/60 transition-colors hover:border-red-400 hover:text-red-400">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "applications" && (
          <div data-testid="admin-applications-tab" className="space-y-4">
            <h2 className="font-display text-3xl font-light uppercase tracking-tight text-ivory">Applications</h2>
            {applications.length === 0 && <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">No applications yet.</p>}
            {applications.map((a) => (
              <div key={a.id} data-testid={`admin-application-${a.id}`} className="border border-white/10 bg-[#121212] p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <span className="font-syne text-base font-semibold uppercase tracking-wide text-white">{a.name}</span>
                    <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{a.division}</span>
                  </div>
                  <StatusSelect kind="application" id={a.id} value={a.status} onChange={patchStatus("applications", setApplications)} />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-[11px] text-white/55 sm:grid-cols-4">
                  <span>{a.email}</span><span>Age {a.age || "—"}</span><span>{a.location || "—"}</span><span>{a.height || ""}</span>
                </div>
                {a.message && <p className="mt-3 text-sm text-white/60">{a.message}</p>}
                {a.links && <p className="mt-2 font-mono text-[11px] text-gold/80">{a.links}</p>}
                {a.files && (a.files.photos?.length > 0 || a.files.resume || a.files.demos?.length > 0) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(a.files.photos || []).map((f) => (
                      <a key={f} href={`${UPLOADS}/${f}`} target="_blank" rel="noreferrer" className="border border-white/15 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-white/60 hover:border-gold hover:text-gold">Photo</a>
                    ))}
                    {a.files.resume && (
                      <a href={`${UPLOADS}/${a.files.resume}`} target="_blank" rel="noreferrer" className="border border-white/15 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-white/60 hover:border-gold hover:text-gold">Résumé</a>
                    )}
                    {(a.files.demos || []).map((f) => (
                      <a key={f} href={`${UPLOADS}/${f}`} target="_blank" rel="noreferrer" className="border border-white/15 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-white/60 hover:border-gold hover:text-gold">Demo</a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "enquiries" && (
          <div data-testid="admin-enquiries-tab" className="space-y-4">
            <h2 className="font-display text-3xl font-light uppercase tracking-tight text-ivory">Client Enquiries</h2>
            {enquiries.length === 0 && <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">No enquiries yet.</p>}
            {enquiries.map((q) => (
              <div key={q.id} data-testid={`admin-enquiry-${q.id}`} className="border border-white/10 bg-[#121212] p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <span className="font-syne text-base font-semibold uppercase tracking-wide text-white">{q.client_name}</span>
                    <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{q.enquiry_type}</span>
                  </div>
                  <StatusSelect kind="enquiry" id={q.id} value={q.status} onChange={patchStatus("enquiries", setEnquiries)} />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-[11px] text-white/55 sm:grid-cols-4">
                  <span>{q.email}</span><span>{q.company || "—"}</span><span>{q.dates || "—"}</span><span>{q.budget || "—"}</span>
                </div>
                {q.shortlist?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {q.shortlist.map((n) => (
                      <span key={n} className="border border-gold/40 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-gold">{n}</span>
                    ))}
                  </div>
                )}
                {q.brief && <p className="mt-3 text-sm text-white/60">{q.brief}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === "contacts" && (
          <div data-testid="admin-contacts-tab" className="space-y-4">
            <h2 className="font-display text-3xl font-light uppercase tracking-tight text-ivory">Contact Messages</h2>
            {contacts.length === 0 && <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">No messages yet.</p>}
            {contacts.map((c) => (
              <div key={c.id} data-testid={`admin-contact-${c.id}`} className="border border-white/10 bg-[#121212] p-6">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-syne text-base font-semibold uppercase tracking-wide text-white">{c.name}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{c.pathway}</span>
                  <span className="font-mono text-[11px] text-white/50">{c.email}</span>
                </div>
                <p className="mt-3 text-sm text-white/60">{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
